<script setup lang="ts">
import { ref } from 'vue';
import { uniqueId } from 'lodash-es';
import PlusOutlineIcon from '@/assets/icons/shared/plus-outline.svg';
import BankDetailsCard from '@/modules/counterparty/counterparty-master/components/BankDetailsCard.vue';
import BankDetailsFormModal from '@/modules/counterparty/counterparty-master/components/BankDetailsFormModal.vue';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import type {
  CounterpartyBankDetailsFormData,
  CounterpartyBankDetailsItem,
} from '@/modules/counterparty/counterparty-master/types/bank-details-form.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { useWizardOptionalStep } from '@/providers/wizard.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCardList from '@/modules/ui/components/ui/UiCardList.vue';

useWizardOptionalStep();

const { formData, submittedItemIds } = useCounterpartyMaster();

const isFormOpen = ref(false);
const editItem = ref<CounterpartyBankDetailsItem | null>(null);

function openForm(item: CounterpartyBankDetailsItem | null = null) {
  editItem.value = item;
  isFormOpen.value = true;
}

function onSubmit(data: CounterpartyBankDetailsFormData) {
  if (editItem.value) {
    const editingId = editItem.value.id;

    submittedItemIds.delete(editingId);
    formData.value = {
      ...formData.value,
      bankDetails: formData.value.bankDetails.map((account) =>
        account.id === editingId
          ? {
              ...account,
              bank: data.bank,
              bankIdentificationCode: data.bankIdentificationCode,
              correspondentAccount: data.correspondentAccount,
              currentAccount: data.currentAccount,
              comment: data.comment,
            }
          : account
      ),
    };
    isFormOpen.value = false;
    return;
  }

  formData.value = {
    ...formData.value,
    bankDetails: [
      ...formData.value.bankDetails,
      {
        id: uniqueId(IdPrefix.Control),
        bank: data.bank,
        bankIdentificationCode: data.bankIdentificationCode,
        correspondentAccount: data.correspondentAccount,
        currentAccount: data.currentAccount,
        comment: data.comment,
      },
    ],
  };
  isFormOpen.value = false;
}

function removeAccount(id: string) {
  submittedItemIds.delete(id);
  formData.value = {
    ...formData.value,
    bankDetails: formData.value.bankDetails.filter((account) => account.id !== id),
  };
}
</script>

<template>
  <div class="step-bank-details">
    <div
      class="list"
      :class="{ _empty: !formData.bankDetails.length }"
    >
      <UiCardList
        v-if="formData.bankDetails.length"
        class="cards"
        :list="formData.bankDetails"
        with-add
        equal-height
        @add="openForm()"
      >
        <template #card="{ item }">
          <BankDetailsCard
            :item="item"
            @edit="openForm(item)"
            @remove="removeAccount(String(item.id))"
          />
        </template>
      </UiCardList>

      <UiButton
        v-else
        color="black"
        mode="outline"
        @click="openForm()"
      >
        <PlusOutlineIcon v-prefix />
        <span>{{ $t('counterparty.master.steps.bankDetails.add') }}</span>
      </UiButton>
    </div>

    <BankDetailsFormModal
      v-model="isFormOpen"
      :edit-item="editItem"
      @submit="onSubmit"
    />
  </div>
</template>

<style scoped lang="scss">
.step-bank-details {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.list {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  border: var(--border-dashed-gray-400);
  border-radius: 8px;

  &._empty {
    justify-content: center;
    align-items: center;
  }
}

.cards {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
}

.list .cards {
  grid-template-columns: repeat(auto-fill, minmax(min(140px, 100%), 1fr));
}
</style>
